import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { format } from 'date-fns';
import { useRecoilState } from 'recoil';

import type { APNotification } from '../utils';
import {
  appendCorrelationToken,
  findItem,
  getConflictingEvents,
  handlePlanAppointments as handlePlanAppointmentsUtil,
  notificationsToHashTable,
  planningEntitiesToEvents,
  sortByDate,
  sourceViewportContext,
  unassignAppointment as unassignAppointmentUtil
} from '../utils';

import { mutations } from 'RestAPIs/rapini';

import {
  dialogManagerActiveAtom,
  frozenCardDropAtom,
  planResponsePlanningEntityIdentifiersAtom,
  shouldShowPopoverDialogAtom
} from '../atoms';

import { Box } from '@mui/material';

import { LoadingOverlay } from 'components/shared';

import { showErrorSnackbar } from 'components/shared/Snackbar';
import TaskList, { TASK_LIST_WIDTH } from 'components/shared/TaskList/TaskList';
import { PopOverConfirmationContext } from '../PopOverConfirmationContext';
import DragPortalPragmatic from './DragPortalPragmatic/DragPortalPragmatic';
import FrozenDropCardPragmatic from './FrozenDropCardPragmatic';
import RouteOverviewPragmatic from './RouteOverviewPragmatic/RouteOverviewPragmatic';
import SortedToPlanListPragmatic from './SortedToPlanListPragmatic/SortedToPlanListPragmatic';
import ToPlanDropzonePragmatic from './ToPlanDropzonePragmatic/ToPlanDropzonePragmatic';

import type { PlanAppointmentsRequest } from 'generated';
import type { ConnectAppointmentGroupingPayload } from 'RestAPIs/activities';
import type { EditAppointmentType } from 'RestAPIs/activities/types';
import { DialogType } from '../components/DialogManager/types';
import { DROP_LOADING_OVERLAY_Z_INDEX } from '../frozenDrop.constants';
import { useFrozenDrop } from '../hooks/useFrozenDrop';
import type { UsePlanningReturn } from '../hooks/usePlanning';
import type { Event } from '../types';

// Suppress the browser's default drag cursors (the green "+" copy badge, the
// forbidden circle, etc.) and force a consistent "move" cursor across the
// whole window for the entire duration of any HTML5 drag.
//
// Registered at module load (NOT inside a useEffect) so the listeners exist
// before the user can possibly start a drag, and registered in the capture
// phase so we run BEFORE Pragmatic's own document-level listeners. Without
// this, the browser briefly shows its default initial cursor for the first
// frame of the drag, since `effectAllowed` is otherwise unset.
// Use a property on window as a one-time guard so that Vite HMR module
// re-evaluation and test environments that re-import this module do not
// register duplicate listeners.
declare global {
  interface Window {
    __cardsViewPragmaticDragListenersInstalled?: boolean;
  }
}
if (
  typeof document !== 'undefined' &&
  !window.__cardsViewPragmaticDragListenersInstalled
) {
  window.__cardsViewPragmaticDragListenersInstalled = true;
  document.addEventListener(
    'dragstart',
    (event: DragEvent) => {
      if (event.dataTransfer) {
        event.dataTransfer.effectAllowed = 'move';
      }
    },
    { capture: true }
  );
  // Accept every drag as a "move" everywhere — including outside any drop
  // target — so the browser never falls back to the forbidden cursor.
  const acceptAsMove = (event: DragEvent) => {
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }
    event.preventDefault();
  };
  document.addEventListener('dragenter', acceptAsMove, { capture: true });
  document.addEventListener('dragover', acceptAsMove, { capture: true });
}

export default function CardsViewPragmatic({
  startDate,
  endDate,
  currentPlanningEntityIdentifiers,
  planningEntities,
  setPlanningEntities,
  clonedPlanningEntities,
  setClonedPlanningEntities,
  planAppointments,
  connectAppointmentGrouping,
  revertAction,
  isLoading
}: UsePlanningReturn) {
  'use memo';
  const [taskListIsOpen, setTaskListIsOpen] = useState<boolean>(true);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const [shouldShowPopoverDialog, setShouldShowPopoverDialog] = useRecoilState(
    shouldShowPopoverDialogAtom
  );

  const [activeEvent, setActiveEvent] = useState<Event | null>(null);

  // A single subscription gives both the setter and the value used below to
  // decide whether the warning dialog is holding the frozen card.
  const [warningDialogState, setDialogState] = useRecoilState(
    dialogManagerActiveAtom
  );
  const [payload, setPayload] = useState<
    PlanAppointmentsRequest | ConnectAppointmentGroupingPayload
  >();

  const [activeEntityContainingSelection, setActiveEntityContainingSelection] =
    useState<string | null>(null);

  const [, setPlanResponsePlanningEntityIdentifiers] = useRecoilState(
    planResponsePlanningEntityIdentifiersAtom
  );

  const [pendingDropPosition, setPendingDropPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);

  const [isLoadingUnassignAppointment, setIsLoadingUnassignAppointment] =
    useState<boolean>(false);

  const { mutateAsync: unassignAppointmentAsync } =
    mutations.useRemoveAssignmentFromAppointments();

  // A recurring drop shows the "this / all occurrences" confirmation popover
  // before any API call fires. The action isn't finished until the user picks
  // an option, so keep the card frozen while that popover is open (alongside the
  // warning-dialog hold via `warningDialogState`).
  const isDragActionPopoverOpen =
    shouldShowPopoverDialog.id !== '' && !!shouldShowPopoverDialog.isDragAction;

  // Frozen drop preview: hold a copy of the card at the drop slot + faded
  // source until the call + refetch settle. See `useFrozenDrop`.
  const { freeze: freezeCardDrop, release: releaseFrozenDrop } = useFrozenDrop(
    frozenCardDropAtom,
    {
      isLoading: isLoading || isLoadingUnassignAppointment,
      holdWhileOpen:
        isDragActionPopoverOpen ||
        (warningDialogState.open &&
          warningDialogState.type === DialogType.WARNING)
    }
  );

  const onMutationError = () => {
    containerRef.current?.removeAttribute('data-drag-active');
    setPayload(undefined);
    releaseFrozenDrop();
    revertAction();
    showErrorSnackbar();
    setPendingDropPosition(null);
  };

  const handlePlanAppointments = (payload: PlanAppointmentsRequest | null) => {
    handlePlanAppointmentsUtil({
      payload,
      planAppointments,
      setPayload,
      setShouldShowPopoverDialog,
      setDialogState,
      revertAction,
      onMutationError,
      planningEntitiesToEvents,
      getConflictingEvents,
      notificationsToHashTable,
      planningEntities,
      appendCorrelationToken,
      setPlanResponsePlanningEntityIdentifiers,
      setPlanningEntities,
      viewport: {
        startDate,
        endDate,
        planningEntityIdentifiers: currentPlanningEntityIdentifiers
      },
      onCancelWarningDialog: () => {
        releaseFrozenDrop();
        setPendingDropPosition(null);
        setActiveEvent(null);
      },
      onSuccess: () => {
        containerRef.current?.removeAttribute('data-drag-active');
        setSelectedIds([]);
        setActiveEntityContainingSelection(null);
        setPendingDropPosition(null);
        setActiveEvent(null);
        // Frozen frame is released by the loading-settled effect, so it clears
        // exactly when the card snaps to its final position.
      }
    });
  };

  const sendConnectAppointmentGrouping = (
    appointmentGroupingEntityId: string
  ) => {
    if (planningEntities) {
      const payload: ConnectAppointmentGroupingPayload = {
        appointmentGroupingId: appointmentGroupingEntityId,
        appointmentGroupingStart: startDate,
        editAppointmentType: 'ONLY_THIS',
        suppressWarnings: false
      };
      const connectAppointmentPayload = appendCorrelationToken(payload);
      setPayload(connectAppointmentPayload);

      const planningEntity = planningEntities[appointmentGroupingEntityId];
      const today = new Date();
      today.setUTCHours(0, 0, 0, 0);
      if (
        planningEntity.schedules
          .map((s) => s.events)
          .flat()
          .some((event) => event.isRecurring && !event.isAbsent) &&
        new Date(startDate) >= today
      ) {
        setShouldShowPopoverDialog({
          id: appointmentGroupingEntityId,
          scheduleStart: startDate
        });
      } else {
        connectAppointmentGrouping(connectAppointmentPayload, {
          onSuccess: (response: any) => {
            if (!response.data.success) {
              const notifications = response.data
                .notifications as APNotification[];

              const events =
                planningEntities[
                  connectAppointmentPayload.appointmentGroupingId
                ].schedules
                  .map((s) => s.events)
                  .flat() ?? [];
              const conflictingEvents: Event[] = getConflictingEvents(
                events || [],
                notifications
              );

              setDialogState({
                type: DialogType.WARNING,
                open: true,
                data: notificationsToHashTable(
                  conflictingEvents,
                  notifications
                ),
                onSubmit: () => {
                  connectAppointmentGrouping(
                    {
                      ...connectAppointmentPayload,
                      suppressWarnings: true
                    } as ConnectAppointmentGroupingPayload,
                    {
                      onError: onMutationError
                    }
                  );
                },
                onCancel: () => revertAction()
              });
            }
          },
          onError: onMutationError
        });
      }
    }
  };

  function unassignAppointment(editAppointmentType: EditAppointmentType) {
    unassignAppointmentUtil({
      editAppointmentType,
      activeEvent,
      selectedIds,
      planningEntities,
      shouldShowPopoverDialog,
      appendCorrelationToken,
      unassignAppointmentAsync,
      setShouldShowPopoverDialog,
      setPlanResponsePlanningEntityIdentifiers,
      setActiveEntityContainingSelection,
      setIsLoading: setIsLoadingUnassignAppointment,
      onFinally: () => {
        containerRef.current?.removeAttribute('data-drag-active');
        setIsLoadingUnassignAppointment(false);
        setActiveEvent(null);
        setActiveEntityContainingSelection(null);
        setPendingDropPosition(null);
        setSelectedIds([]);
      },
      onGetEvents: (planningEntity, isToPlan, selectedIds) => {
        return (
          (isToPlan
            ? planningEntity?.events.filter(({ id }: { id: string }) =>
                selectedIds.includes(id)
              )
            : planningEntity?.schedules
                .flatMap((s: any) => s.events)
                .filter(({ id }: { id: string }) =>
                  selectedIds.includes(id)
                )) || []
        );
      }
    });
  }

  // --- Stable handler refs to avoid stale closures in the monitor ---
  const handleDragStartRef = useRef<(source: any) => void>(() => {});
  const handleDragEndRef = useRef<(source: any, location: any) => void>(
    () => {}
  );
  const containerRef = useRef<HTMLDivElement | null>(null);

  const planningEntitiesArray = planningEntities
    ? Object.values(planningEntities)
    : [];

  function handleDragStart(source: any) {
    const activePlanningEntityId = source.data?.planningEntityId;
    const activeId = source.data?.id;

    // Guard: ignore drags that don't carry our contract (e.g. external drags)
    if (!activePlanningEntityId || !activeId) return;

    containerRef.current?.setAttribute('data-drag-active', '');

    const isToPlan = source.data.isToPlan;

    const event = !isToPlan
      ? planningEntities?.[activePlanningEntityId]?.schedules
          .map((s) => s.events)
          .flat()
          .find(({ id }) => id === activeId) ?? null
      : planningEntities?.[activePlanningEntityId]?.events.find(
          ({ id }) => id === activeId
        ) ?? null;

    setActiveEvent(event);
    setActiveEntityContainingSelection(activePlanningEntityId);
    // Use `prev` from the functional updater to avoid a stale closure on `selectedIds`
    setSelectedIds((prev) => (prev.includes(activeId) ? prev : []));
    setClonedPlanningEntities(planningEntities);
  }

  function handleDragEnd(source: any, location: any) {
    const dropTargets: any[] = location.current.dropTargets;

    // No drop target — treat as cancel
    if (dropTargets.length === 0) {
      containerRef.current?.removeAttribute('data-drag-active');
      if (clonedPlanningEntities) {
        setPlanningEntities(clonedPlanningEntities);
      }
      setActiveEvent(null);
      setPendingDropPosition(null);
      setClonedPlanningEntities(null);
      return;
    }

    const overData = dropTargets[0].data;
    const overElement = dropTargets[0].element as HTMLElement | undefined;
    const activeData = source.data;

    // Guard: ignore drags that don't carry our contract
    if (!activeData?.planningEntityId || !activeData?.id) return;

    const activePlanningEntityId = activeData.planningEntityId;
    const activeIsToPlan = activeData.isToPlan;
    const overIsToPlan = overData.isToPlan;

    if (activeIsToPlan === overIsToPlan) {
      containerRef.current?.removeAttribute('data-drag-active');
      setPendingDropPosition(null);
      return;
    }

    if (activePlanningEntityId === undefined || !activeEvent) {
      containerRef.current?.removeAttribute('data-drag-active');
      setActiveEvent(null);
      setPendingDropPosition(null);
      setSelectedIds([]);
      return;
    }

    const activeId = activeData.id;

    const ids = selectedIds.length ? selectedIds : [activeId];

    const selectedEvents: Event[] = ids
      .map((id) => findItem(planningEntities, activePlanningEntityId, id))
      .filter((e): e is Event => e != null);

    const occurrenceDate = activeEvent.start
      ? format(new Date(activeEvent.start), 'y-MM-dd')
      : startDate;

    const today = new Date();
    today.setUTCHours(0, 0, 0, 0);

    const dropPayload: PlanAppointmentsRequest = {
      appointmentOccurrences:
        selectedIds.length === 0
          ? [
              {
                entityId: activeEvent.id,
                originalOccurrenceStart: activeEvent.start,
                schedulingLabel: activeEvent.schedulingLabel
              }
            ]
          : sortByDate(
              selectedEvents.map((event: Event) => ({
                entityId: event.id,
                originalOccurrenceStart: event.start,
                schedulingLabel: event.schedulingLabel
              })),
              'originalOccurrenceStart'
            ),
      planningEntity: {
        ...overData.planningEntityIdentifier,
        start: overIsToPlan ? undefined : overData.scheduleStart
      },
      start: overData.start,
      editAppointmentType: 'ONLY_THIS',
      suppressWarnings: false,
      // The row being dragged out of. Completed with the board's dates and rows in handlePlanAppointments.
      viewport: sourceViewportContext(planningEntities, activePlanningEntityId)
    };

    // Freeze the last drag frame: hold a copy of the card at the drop slot +
    // faded source until the call + refetch settle. The final position is
    // determined by where the card is dropped (the route recomputes its start),
    // so anchor to the drop target's top-left and match its width.
    const freezeDrop = () => {
      const rect = overElement?.getBoundingClientRect();
      const input = location.current.input;
      const position = rect
        ? { x: rect.left, y: rect.top }
        : { x: input.clientX, y: input.clientY };
      freezeCardDrop({
        eventIds: ids,
        position,
        width: rect?.width ?? 0,
        count: ids.length,
        event: activeEvent,
        planningEntityId: activePlanningEntityId,
        planningEntityType: activeData.planningEntityType
      });
    };

    if (activeEvent?.isRecurring && new Date(occurrenceDate) >= today) {
      // Recurring: freeze the card at the drop slot and keep it there while the
      // confirmation popover is open (held via `isDragActionPopoverOpen`), so it
      // doesn't vanish before the user picks an occurrence option.
      freezeDrop();
      // Un-dim the other cards immediately (popover is about to show, no API
      // call yet); the frozen source stays faded on its own.
      containerRef.current?.removeAttribute('data-drag-active');
      // Use the drop position from Pragmatic's location to anchor the confirmation popover
      const input = location.current.input;
      setPayload(dropPayload);
      setPendingDropPosition({ x: input.clientX, y: input.clientY });
      setShouldShowPopoverDialog({
        id: activeEvent.id,
        scheduleStart: overData.scheduleStart,
        isUnassignment: overIsToPlan,
        isDragAction: true
      });
    } else if (overIsToPlan) {
      freezeDrop();
      unassignAppointment('ONLY_THIS');
    } else {
      freezeDrop();
      handlePlanAppointments(dropPayload);
    }
  }

  // Keep refs pointing at the latest closures. Assigned in a layout effect
  // (never during render) so the React Compiler can optimize this component;
  // useLayoutEffect runs synchronously after commit, so the monitor never
  // dispatches into a stale `.current` right after a render.
  useLayoutEffect(() => {
    handleDragStartRef.current = handleDragStart;
    handleDragEndRef.current = handleDragEnd;
  });

  // Register the Pragmatic monitor once; use refs to call current handlers
  useEffect(() => {
    return monitorForElements({
      onDragStart: ({ source }) => handleDragStartRef.current(source),
      onDrop: ({ source, location }) =>
        handleDragEndRef.current(source, location)
    });
  }, []);

  return (
    <PopOverConfirmationContext.Provider
      value={{
        isLoading,
        onClose: () => {
          releaseFrozenDrop();
          setActiveEvent(null);
          setPendingDropPosition(null);
          revertAction();
        },
        onHandleAppointmentConfirmation: (
          editAppointmentType: EditAppointmentType,
          isUnassignment?: boolean
        ) => {
          if (isUnassignment) {
            return unassignAppointment(editAppointmentType);
          }

          if (payload && 'appointmentOccurrences' in payload) {
            handlePlanAppointments({
              ...(payload as PlanAppointmentsRequest),
              editAppointmentType
            });
          } else {
            const connectAppointmentPayload = appendCorrelationToken(
              payload as ConnectAppointmentGroupingPayload
            );
            connectAppointmentGrouping(
              {
                ...connectAppointmentPayload,
                editAppointmentType
              } as ConnectAppointmentGroupingPayload,
              {
                onSuccess: (response: any) => {
                  if (
                    !response.data.success &&
                    connectAppointmentPayload &&
                    'appointmentGroupingId' in connectAppointmentPayload &&
                    planningEntities
                  ) {
                    const events =
                      planningEntities[
                        connectAppointmentPayload.appointmentGroupingId
                      ].schedules
                        .map((s) => s.events)
                        .flat() ?? [];
                    const notifications = response.data
                      .notifications as APNotification[];
                    const conflictingEvents: Event[] = getConflictingEvents(
                      events || [],
                      notifications
                    );

                    setDialogState({
                      type: DialogType.WARNING,
                      open: true,
                      data: notificationsToHashTable(
                        conflictingEvents,
                        notifications
                      ),
                      onSubmit: () => {
                        connectAppointmentGrouping(
                          {
                            ...connectAppointmentPayload,
                            editAppointmentType,
                            suppressWarnings: true
                          } as ConnectAppointmentGroupingPayload,
                          {
                            onError: onMutationError
                          }
                        );
                      },
                      onCancel: () => {
                        revertAction();
                        setActiveEvent(null);
                      }
                    });
                  } else {
                    setActiveEvent(null);
                  }
                },
                onError: onMutationError
              }
            );
          }
        }
      }}
    >
      <LoadingOverlay
        loading={isLoading || isLoadingUnassignAppointment}
        zIndex={DROP_LOADING_OVERLAY_Z_INDEX}
      />
      <Box
        ref={containerRef}
        display="flex"
        position="relative"
        overflow="hidden"
        height="100%"
      >
        <Box
          sx={{
            width: '100%',
            height: '100%',
            overflowX: 'auto',
            minWidth: taskListIsOpen ? 'initial' : '100%',
            maxWidth: taskListIsOpen
              ? `calc(100% - ${TASK_LIST_WIDTH}px)`
              : 'initial',
            position: 'relative',
            top: 0,
            borderLeft: (theme) => `1px solid ${theme.palette.divider}`
          }}
        >
          <RouteOverviewPragmatic
            activeEntityContainingSelection={activeEntityContainingSelection}
            onChangeSelection={setSelectedIds}
            onChangeActiveEntity={setActiveEntityContainingSelection}
            planningEntities={planningEntitiesArray}
            connectAppointmentGrouping={sendConnectAppointmentGrouping}
          />
        </Box>

        <TaskList
          onOpen={setTaskListIsOpen.bind(undefined, true)}
          onClose={setTaskListIsOpen.bind(undefined, false)}
          planningEntities={planningEntities}
          dropzonePadding="10px"
          activeEntityContainingSelection={activeEntityContainingSelection}
          onChangeActiveEntity={setActiveEntityContainingSelection}
          onChangeSelection={setSelectedIds}
          DropzoneComponent={ToPlanDropzonePragmatic}
          SortedToPlanListComponent={SortedToPlanListPragmatic}
        />
      </Box>
      {activeEvent && pendingDropPosition && (
        <DragPortalPragmatic
          activeEvent={activeEvent}
          selectedIds={selectedIds}
          pendingDropPosition={pendingDropPosition}
        />
      )}
      <FrozenDropCardPragmatic />
    </PopOverConfirmationContext.Provider>
  );
}
